/**
 * 支付服务
 * 处理支付相关的业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  Package,
  CreateOrderParams,
  CreateOrderResponse,
  PaymentQRCode,
  OrderDetail,
  ActivateCodeParams,
  ActivateCodeResponse,
  GetCouponResponse,
  Coupon,
  MiniProgramPaymentParams,
  MiniProgramPaymentResponse,
  MiniProgramVirtualPaymentParams,
  MiniProgramVirtualPaymentResponse,
  MiniProgramVirtualPaymentExecuteOptions,
  MiniProgramVirtualPaymentPollingOptions,
  MiniProgramVirtualPaymentRuntime,
  MiniProgramVirtualPaymentWxRuntime,
  PaymentChannelAdapter,
  PaymentChannelOrder
} from '../../types'
import { PaymentAPI } from '../api/payment'
import { PaymentStore } from '../stores/PaymentStore'
import { BaseService } from './BaseService'
import { createPolling, type Polling } from '../utils/polling'
import {
  getApplet,
  getMobilePayUrl,
  getWxMobilePayUrl,
  getNativePayRedirect,
  getSoftId,
  getVirtualPayEnv,
  getPaymentConfig,
  getSdk360PaymentConfig,
  updateSDKConfig
} from '../config/SDKConfigManager'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { DeviceInfoManager } from './DeviceInfoManager'
import { DeviceStore } from '../stores/DeviceStore'
import { SourceStore } from '../stores/SourceStore'
import { isMiniWechatWebView, isWeChat, getDeviceEnvironment, isAppWebView } from '../utils/device'
import { extractStoredAuthToken } from '../utils/authToken'
import QRCode from 'qrcode'
import {
  createSdk360PaymentQRCode,
  isSdk360PaymentConfigured
} from './Sdk360PaymentAdapter'

/** 支付跳转结果 */
export interface PaymentRedirectResult {
  /** 是否已处理跳转（true 表示已跳转，调用方应直接 return） */
  handled: boolean
  /** 跳转类型：native-原生跳转, h5-H5页面跳转, desktop-桌面软件, pc-PC端显示二维码 */
  type: 'native' | 'h5' | 'desktop' | 'pc'
  /** 订单 ID */
  orderId?: string
  /** 二维码图片（仅 PC 端） */
  qrCodeImage?: string
  /** 后端返回的 H5 支付 URL（如果存在），具体跳转由业务项目处理 */
  paymentUrl?: string
}

/** 桌面软件支付参数 */
export interface DesktopPayParams {
  /** 用户 ID */
  userId: string
  /** 套餐 ID */
  productId: number
  /** 套餐名称 */
  productDescription: string
}

export interface PaymentPollingHandle {
  promise: Promise<void>
  stop: () => void
}

export const MINI_PROGRAM_VIRTUAL_PAYMENT_SESSION_EXPIRED_CODE = -15007

const MINI_PROGRAM_VIRTUAL_PAYMENT_CANCEL_CODE = -2
/*
const MINI_PROGRAM_VIRTUAL_PAYMENT_ERROR_MAP: Record<number, string> = {
  [-1]: '支付失败',
  [-2]: '已取消支付',
  [-4]: '风控拦截，暂时无法支付',
  [-5]: '开通签约结果未知',
  [-15001]: '参数错误',
  [-15002]: '订单号重复，请重新下单',
  [-15003]: '系统错误，请稍后重试',
  [-15005]: '用户态签名错误',
  [-15006]: '支付签名错误',
  [-15007]: '登录态过期，请重试',
  [-15009]: '代币未发布',
  [-15010]: '道具未发布',
  [-15012]: '订单已关闭，请重新下单',
  [-15013]: '道具价格错误',
  [-15014]: '道具发布未生效，请稍后重试',
  [-15016]: '签名数据格式错误',
  [-15020]: '操作过快，请稍候再试',
  [-15021]: '交易频率受限，请稍后重试'
}

*/
const MINI_PROGRAM_VIRTUAL_PAYMENT_ERROR_MAP: Record<number, string> = {
  [-1]: 'Payment failed',
  [-2]: 'Payment cancelled',
  [-4]: 'Payment blocked by risk control',
  [-5]: 'Contract result unknown',
  [-15001]: 'Invalid payment parameters',
  [-15002]: 'Duplicate order number',
  [-15003]: 'Payment system error',
  [-15005]: 'Invalid user signature',
  [-15006]: 'Invalid payment signature',
  [-15007]: 'Login session expired',
  [-15009]: 'Currency is not published',
  [-15010]: 'Product is not published',
  [-15012]: 'Order is closed',
  [-15013]: 'Invalid product price',
  [-15014]: 'Product publish is not effective yet',
  [-15016]: 'Invalid sign data format',
  [-15020]: 'Too many payment operations',
  [-15021]: 'Payment frequency limited'
}

function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0
    const nb = pb[i] || 0
    if (na !== nb) return na > nb ? 1 : -1
  }
  return 0
}

function getMiniProgramRuntime(): MiniProgramVirtualPaymentRuntime | undefined {
  return (globalThis as any).uni
}

function getWxRuntime(): MiniProgramVirtualPaymentWxRuntime | undefined {
  return (globalThis as any).wx
}

/** iOS 虚拟支付最低微信客户端版本 */
const IOS_MIN_WECHAT_VERSION = '8.0.68'
/** iOS 虚拟支付最低系统版本 */
const IOS_MIN_SYSTEM_VERSION = '15'

/**
 * 检查 iOS 设备是否满足虚拟支付环境要求
 * 条件：iOS 15+、微信 8.0.68+
 * @returns null 表示通过，字符串表示不满足的原因
 */
function checkiOSVirtualPaymentEnv(): string | null {
  const wx = getWxRuntime()
  if (!wx?.getSystemInfoSync) return null

  const info = wx.getSystemInfoSync()
  const platform = (info?.platform || '').toLowerCase()

  // 非 iOS 设备不做额外检查
  if (platform !== 'ios') return null

  // 检查 iOS 系统版本（system 格式如 "iOS 17.0"）
  const systemMatch = (info?.system || '').match(/(\d+[\d.]*)/)?.[1]
  if (systemMatch && compareVersion(systemMatch, IOS_MIN_SYSTEM_VERSION) < 0) {
    return `当前系统版本过低，Apple 支付需要 iOS ${IOS_MIN_SYSTEM_VERSION} 及以上`
  }

  // 检查微信客户端版本
  const wechatVersion = info?.version || ''
  if (wechatVersion && compareVersion(wechatVersion, IOS_MIN_WECHAT_VERSION) < 0) {
    return `当前微信版本过低，Apple 支付需要微信 ${IOS_MIN_WECHAT_VERSION} 及以上`
  }

  return null
}

export class PaymentService extends BaseService {
  private paymentAPI: PaymentAPI
  private paymentStore: PaymentStore
  private polling: Polling | null = null
  private paymentChannel: PaymentChannelAdapter | null = null

  constructor(httpClient: HttpClient, storage: IStorage) {
    super(httpClient, storage)
    // 创建 DeviceInfoManager 用于获取公共参数
    const deviceStore = new DeviceStore(storage)
    const sourceStore = new SourceStore(storage)
    const deviceInfoManager = new DeviceInfoManager(deviceStore, sourceStore, storage)
    this.paymentAPI = new PaymentAPI(httpClient, deviceInfoManager)
    this.paymentStore = new PaymentStore(storage)
  }

  /**
   * 获取 PaymentStore 实例
   */
  getPaymentStore(): PaymentStore {
    return this.paymentStore
  }

  /**
   * 开启或关闭 360 支付出码。关闭后继续使用原有支付二维码逻辑。
   */
  setSdk360PaymentEnabled(enabled: boolean): void {
    const paymentConfig = getPaymentConfig() || {}
    const current = getSdk360PaymentConfig() || {}
    updateSDKConfig({
      payment: {
        ...paymentConfig,
        sdk360: {
          ...current,
          enabled
        }
      }
    })
  }

  /**
   * 当前是否启用 360 支付出码。
   */
  isSdk360PaymentEnabled(): boolean {
    return isSdk360PaymentConfigured(getSdk360PaymentConfig())
  }

  /** 注册并初始化宿主提供的外部支付渠道。 */
  async registerPaymentChannel(channel: PaymentChannelAdapter): Promise<void> {
    if (!channel?.id?.trim() || typeof channel.createPaymentTarget !== 'function') {
      throw new Error('支付渠道必须提供 id 和 createPaymentTarget()')
    }
    if (channel.isAvailable && !(await channel.isAvailable())) {
      throw new Error(`支付渠道不可用：${channel.id}`)
    }

    await channel.initialize?.()
    const previous = this.paymentChannel
    this.paymentChannel = channel
    if (previous && previous !== channel) await previous.release?.()
  }

  /** 当前是否存在已注册的外部支付渠道。 */
  hasPaymentChannel(): boolean {
    return this.paymentChannel !== null
  }

  /** 释放当前外部支付渠道。 */
  async releasePaymentChannel(): Promise<void> {
    const channel = this.paymentChannel
    this.paymentChannel = null
    await channel?.release?.()
  }

  /**
   * 获取套餐列表
   */
  async getPackages(): Promise<Package[]> {
    try {
      const packages = await this.paymentAPI.getPackages(this.getProductPriceQueryParams())
      this.paymentStore.packages = packages
      return packages
    } catch (error) {
      console.error('[PaymentService] Failed to get packages', error)
      throw error
    }
  }

  /**
   * 获取 Token 套餐列表
   */
  async getTokenPackages(): Promise<Package[]> {
    try {
      return await this.paymentAPI.getTokenPackages()
    } catch (error) {
      console.error('[PaymentService] Failed to get token packages', error)
      throw error
    }
  }

  /**
   * 获取积分套餐列表
   */
  async getPointPackages(): Promise<Package[]> {
    try {
      return await this.paymentAPI.getPointPackages()
    } catch (error) {
      console.error('[PaymentService] Failed to get point packages', error)
      throw error
    }
  }

  /**
   * 获取用户 Tokens 余额
   */
  async getUserTokens(): Promise<{ tokens: number }> {
    try {
      const tokenRaw = this.storage.getItem<string>(STORAGE_KEYS.TOKEN)
      const token = extractStoredAuthToken(tokenRaw)
      return await this.paymentAPI.getUserTokens(token || undefined)
    } catch (error) {
      console.error('[PaymentService] Failed to get user tokens', error)
      throw error
    }
  }

  /**
   * 获取订阅套餐列表
   */
  async getSubscribePackages(): Promise<Package[]> {
    try {
      return await this.paymentAPI.getSubscribePackages()
    } catch (error) {
      console.error('[PaymentService] Failed to get subscribe packages', error)
      throw error
    }
  }

  /**
   * 取消订阅套餐
   */
  async cancelSubscribe(subscribeId: string): Promise<void> {
    try {
      await this.paymentAPI.cancelSubscribe(subscribeId)
    } catch (error) {
      console.error('[PaymentService] Failed to cancel subscribe', error)
      throw error
    }
  }

  /**
   * 创建订单
   */
  async createOrder(params: CreateOrderParams): Promise<CreateOrderResponse> {
    try {
      const result = await this.paymentAPI.createOrder(params)
      this.paymentStore.currentOrder = result.order
      return result
    } catch (error) {
      console.error('[PaymentService] Failed to create order', error)
      throw error
    }
  }

  async getOrderDetail(orderId: number | string): Promise<OrderDetail> {
    try {
      return await this.paymentAPI.getOrderDetail(orderId)
    } catch (error) {
      console.error('[PaymentService] Failed to get order detail', error)
      throw error
    }
  }

  async checkPaymentStatus(orderId: string) {
    try {
      return await this.paymentAPI.checkPaymentStatus(orderId)
    } catch (error) {
      console.error('[PaymentService] Failed to check payment status', error)
      throw error
    }
  }

  /**
   * 获取支付二维码
   * 根据设备环境自动选择 API：
   * - 移动端（phone-web/phone-wechat）：调用 createOrder (POST /api/order)，返回带 mobile: true 标记
   * - PC 端（pc-web/pc-wechat/desktop-app）：调用 getPaymentQRCode (GET /api/wechat-qr)
   * - 小程序/App WebView：调用 getPaymentQRCode (GET)，由调用方判断环境后跳转原生支付
   *
   * 注意：小程序/App 环境的跳转逻辑应由调用方处理，本方法只负责获取支付数据
   */
  async getPaymentQRCode(params: {
    plan_type: number
    goods_id: number
    coupon_code?: string
  }): Promise<PaymentQRCode> {
    try {
      const device = getDeviceEnvironment()

      console.log('[PaymentService] Device environment:', device)

      // 移动端浏览器（手机 Web / 手机微信）走 POST 创建订单，返回带 mobile 标记
      const isMobileDevice = device === 'phone-web' || device === 'phone-wechat'

      let qrCode: PaymentQRCode

      if (isMobileDevice) {
        // 移动端：调用 createOrder 接口（对应旧版 getPayCodeUrl）
        const orderResult = await this.paymentAPI.createOrder({
          plan_type: params.plan_type,
          package_id: params.goods_id,
          coupon_code: params.coupon_code
        })

        // 兼容两种返回格式：
        // 1. 标准格式：{ order: { id, qr_code_url, ... } }
        // 2. 简化格式：{ id, order_id, order_url, price, ... }（直接返回订单信息）
        const order = orderResult.order || orderResult
        const rawResult = orderResult as any

        // 提取支付链接，优先级：order_url > qr_code_url > payment_url
        const paymentUrl =
          rawResult.order_url ||
          order.qr_code_url ||
          rawResult.qr_code_url ||
          rawResult.payment_url ||
          ''

        // 转换为 PaymentQRCode 格式，添加 mobile 和 id 标记
        qrCode = {
          order_id: String(rawResult.order_id || order.id || rawResult.id),
          url: paymentUrl,
          url_alipay: '',
          order_url: paymentUrl,
          order_sn: rawResult.order_sn || rawResult.order_id || '',
          qr_code_url: paymentUrl,
          qr_code_url_alipay: '',
          qr_code_content: paymentUrl,
          expire_at: order.expire_at || new Date(Date.now() + 5 * 60 * 1000).toISOString(),
          amount: rawResult.price || order.amount || 0,
          price: rawResult.price || order.amount || 0,
          origin_price: rawResult.origin_price || order.original_amount || order.amount || 0,
          status:
            typeof (rawResult.status ?? order.status) === 'number'
              ? (rawResult.status ?? order.status)
              : 0,
          // 移动端标记，用于调用方判断是否需要跳转 H5 支付页
          mobile: true,
          id: rawResult.id || order.id
        } as PaymentQRCode & { mobile: boolean; id: string }
      } else {
        // PC 端 / 桌面 App / 小程序 / App WebView：调用 getPaymentQRCode 接口（对应旧版 getPayQrCode）
        // 小程序/App 环境由调用方判断后跳转原生支付，这里只返回数据
        qrCode = await this.paymentAPI.getPaymentQRCode(params)
        qrCode = await this.applyPartnerPaymentIfEnabled(qrCode, params.goods_id)
      }

      this.paymentStore.paymentQRCode = qrCode
      return qrCode
    } catch (error) {
      console.error('[PaymentService] Failed to get payment QR code', error)
      throw error
    }
  }

  private async applyPartnerPaymentIfEnabled(
    qrCode: PaymentQRCode,
    goodsId: number
  ): Promise<PaymentQRCode> {
    // 与旧版 QCommonUI 保持一致：360 已启用时优先于联想支付。
    if (isSdk360PaymentConfigured(getSdk360PaymentConfig())) {
      return this.applySdk360PaymentIfEnabled(qrCode, goodsId)
    }

    const channel = this.paymentChannel
    if (!channel) {
      return qrCode
    }

    try {
      const pricePackage = await this.resolvePricePackage(goodsId)
      const order = this.createPaymentChannelOrder(qrCode, pricePackage)
      const target = await channel.createPaymentTarget(order)
      if (!target?.url) throw new Error(`支付渠道 ${channel.id} 未返回支付地址`)

      return {
        ...qrCode,
        url: target.url,
        order_url: target.url,
        qr_code_url: target.url,
        qr_code_content: target.url,
        expire_at: target.expiresAt || qrCode.expire_at,
        payment_channel: {
          id: channel.id,
          order,
          metadata: target.metadata
        }
      }
    } catch (error) {
      if (channel.fallbackToStandard !== false) {
        console.warn(`[PaymentService] ${channel.id} payment failed, fallback to standard QR code`, error)
        return qrCode
      }
      throw error
    }
  }

  private async applySdk360PaymentIfEnabled(
    qrCode: PaymentQRCode,
    goodsId: number
  ): Promise<PaymentQRCode> {
    const config = getSdk360PaymentConfig()
    if (!isSdk360PaymentConfigured(config)) {
      return qrCode
    }

    try {
      const pricePackage = await this.resolvePricePackage(goodsId)
      return await createSdk360PaymentQRCode(qrCode, pricePackage, config, getSoftId())
    } catch (error) {
      if (config?.fallbackToStandard !== false) {
        console.warn('[PaymentService] 360 payment failed, fallback to standard QR code', error)
        return qrCode
      }
      throw error
    }
  }

  private getProductPriceQueryParams(): { scene?: string } | undefined {
    return this.isSdk360PaymentEnabled() ? { scene: '1' } : undefined
  }

  private async resolvePricePackage(goodsId: number): Promise<Package | undefined> {
    let pricePackage = this.paymentStore.getPackageById(goodsId)
    if (pricePackage) {
      return pricePackage
    }

    const packages = await this.getPackages()
    pricePackage = packages.find((item) => item.id === goodsId)
    return pricePackage
  }

  private createPaymentChannelOrder(
    qrCode: PaymentQRCode,
    pricePackage: Package | undefined
  ): PaymentChannelOrder {
    const orderNo = String(qrCode.order_sn || qrCode.order_id || '')
    const amountInCents = Math.round(Number(qrCode.price || qrCode.amount || 0) * 100)
    const productId = String(pricePackage?.product_id || getSoftId() || '')
    const productName =
      pricePackage?.product_name || pricePackage?.plan_name || '会员套餐'

    if (!orderNo) throw new Error('外部支付渠道缺少业务订单号')
    if (!Number.isInteger(amountInCents) || amountInCents <= 0) {
      throw new Error('外部支付渠道订单金额无效')
    }
    if (!productId) throw new Error('外部支付渠道缺少商品编码')

    return {
      orderNo,
      amountInCents,
      productId,
      productName,
      description: pricePackage?.hint || ''
    }
  }

  /**
   * 开始支付状态轮询
   */
  async startPaymentPolling(
    orderId: string,
    onSuccess?: () => void,
    onFailed?: () => void
  ): Promise<void> {
    // 停止之前的轮询
    this.stopPaymentPolling()

    // 创建新的轮询实例
    this.polling = createPolling({
      interval: 2000, // 每 2 秒轮询一次
      maxAttempts: 60, // 最多轮询 60 次（2 分钟，兼容旧版）
      stopOnError: false
    })

    try {
      console.log('[PaymentService] Start payment polling for order:', orderId)

      await this.polling.start(
        async () => {
          console.log('[PaymentService] Checking payment status...')
          const status = await this.paymentAPI.checkPaymentStatus(orderId)
          return status
        },
        (status) => {
          // 支付成功
          if (status.status === 'success') {
            console.log('[PaymentService] Payment success')
            // 存储支付成功的订单号，供客服链接携带
            try {
              if (typeof window !== 'undefined' && status.order_id) {
                localStorage.setItem('last_order_id', String(status.order_id))
              }
            } catch {}
            this.paymentStore.clearPaymentInfo()
            onSuccess?.()
            return true
          }

          // 支付失败或取消
          if (status.status === 'failed' || status.status === 'cancelled') {
            console.log('[PaymentService] Payment failed or cancelled')
            this.paymentStore.clearPaymentInfo()
            onFailed?.()
            return true
          }

          // 继续轮询
          return false
        }
      )
    } catch (error: any) {
      console.error('[PaymentService] Payment polling error:', error)
      throw error
    }
  }

  /**
   * 停止支付状态轮询
   */
  stopPaymentPolling(): void {
    if (this.polling) {
      this.polling.stop()
      this.polling = null
      console.log('[PaymentService] Payment polling stopped')
    }
  }

  createPaymentPolling(
    orderId: string,
    options: { interval?: number; maxAttempts?: number } = {}
  ): PaymentPollingHandle {
    const polling = createPolling({
      interval: options.interval ?? 2000,
      maxAttempts: options.maxAttempts ?? 30,
      stopOnError: false
    })

    const promise = polling
      .start(
        async () => this.paymentAPI.checkPaymentStatus(orderId),
        (status) => status.status === 'success'
      )
      .then(() => undefined)
      .catch((error) => {
        if (error?.message === 'Max attempts reached' || error?.message === 'Polling timeout') {
          throw {
            msg: '支付结果确认中，请稍后在会员中心查看',
            timeout: true
          }
        }
        throw error
      })

    return {
      promise,
      stop: () => polling.stop()
    }
  }

  /**
   * 激活码兑换
   */
  async activateCode(params: ActivateCodeParams): Promise<ActivateCodeResponse> {
    try {
      const result = await this.paymentAPI.activateCode(params)

      // 激活成功后，可能需要刷新用户信息
      // 这里不直接调用 UserService，而是让调用方决定是否刷新

      return result
    } catch (error) {
      console.error('[PaymentService] Failed to activate code', error)
      throw error
    }
  }

  /**
   * 领取优惠券
   */
  async getCoupon(couponId: number): Promise<GetCouponResponse> {
    try {
      const result = await this.paymentAPI.getCoupon(couponId)

      // 领取成功后添加到本地
      if (result.success) {
        this.paymentStore.addCoupon(result.coupon)
      }

      return result
    } catch (error) {
      console.error('[PaymentService] Failed to get coupon', error)
      throw error
    }
  }

  /**
   * 领取优惠券（通过 code）
   */
  async getCouponByCode(code: string): Promise<GetCouponResponse> {
    try {
      const result = await this.paymentAPI.getCouponByCode(code)

      // 领取成功后添加到本地
      if (result.success) {
        this.paymentStore.addCoupon(result.coupon)
      }

      return result
    } catch (error) {
      console.error('[PaymentService] Failed to get coupon by code', error)
      throw error
    }
  }

  /**
   * 获取用户优惠券列表
   */
  async getUserCoupons(): Promise<Coupon[]> {
    try {
      const coupons = await this.paymentAPI.getUserCoupons()
      this.paymentStore.coupons = coupons
      return coupons
    } catch (error) {
      console.error('[PaymentService] Failed to get user coupons', error)
      throw error
    }
  }

  /**
   * 小程序支付
   */
  async getMiniProgramPayment(
    params: MiniProgramPaymentParams
  ): Promise<MiniProgramPaymentResponse> {
    try {
      const applet = getApplet()
      const payload = applet && !params.applet ? { ...params, applet } : params
      return await this.paymentAPI.getMiniProgramPayment(payload)
    } catch (error) {
      console.error('[PaymentService] Failed to get mini program payment', error)
      throw error
    }
  }

  async getMiniProgramVirtualPayment(
    params: MiniProgramVirtualPaymentParams
  ): Promise<MiniProgramVirtualPaymentResponse> {
    try {
      const applet = getApplet()
      const payload = applet && !params.applet ? { ...params, applet } : params
      return await this.paymentAPI.getMiniProgramVirtualPayment(payload)
    } catch (error) {
      console.error('[PaymentService] Failed to get mini program virtual payment', error)
      throw error
    }
  }

  canUseMiniProgramVirtualPayment(): boolean {
    const wx = getWxRuntime()
    if (!wx) return false

    const sdkVersion = wx.getAppBaseInfo?.()?.SDKVersion || ''
    if (sdkVersion && compareVersion(sdkVersion, '2.19.2') >= 0) {
      return true
    }

    return wx.canIUse?.('requestVirtualPayment') ?? false
  }

  /*
  getMiniProgramLoginCode(): Promise<string> {
    return new Promise((resolve, reject) => {
      const uni = getMiniProgramRuntime()
      const wx = getWxRuntime()

      if (uni?.login) {
        uni.login({
          provider: 'weixin',
          success: (res) => resolve(res.code),
          fail: (err) => reject({ msg: err?.errMsg || '获取登录 code 失败' })
        })
        return
      }

      if (wx?.login) {
        wx.login({
          success: (res) => resolve(res.code),
          fail: (err) => reject({ msg: err?.errMsg || '获取登录 code 失败' })
        })
        return
      }

      reject({ msg: '仅支持微信小程序环境' })
    })
  }

  requestMiniProgramVirtualPayment(params: MiniProgramVirtualPaymentResponse): Promise<void> {
    return new Promise((resolve, reject) => {
      const wx = getWxRuntime()
      if (!wx?.requestVirtualPayment) {
        reject({ msg: '仅支持微信小程序环境' })
        return
      }

      if (!this.canUseMiniProgramVirtualPayment()) {
        reject({ msg: '当前微信版本不支持虚拟支付，请升级微信' })
        return
      }

      // iOS 环境检查（系统版本、微信版本）
      const iosEnvError = checkiOSVirtualPaymentEnv()
      if (iosEnvError) {
        reject({ msg: iosEnvError, appStoreUnavailable: true })
        return
      }

      wx.requestVirtualPayment({
        mode: params.mode,
        signData: params.sign_data,
        paySig: params.pay_sig,
        signature: params.signature,
        success: () => resolve(),
        fail: (err) => {
          const errCode = err?.errCode ?? 0
          if (errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_CANCEL_CODE) {
            reject({ msg: '已取消支付', cancel: true, errCode })
            return
          }

          if (errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_SESSION_EXPIRED_CODE) {
            reject({
              msg: MINI_PROGRAM_VIRTUAL_PAYMENT_ERROR_MAP[errCode],
              sessionExpired: true,
              errCode
            })
            return
          }

          // iOS App Store 不可用（errCode 4 或 errMsg 包含 "App Store"），直接失败不走轮询
          const errMsg = err?.errMsg || ''
          if (errCode === 4 || errMsg.includes('App Store')) {
            reject({
              msg: '暂时无法提供支付',
              appStoreUnavailable: true,
              errCode
            })
            return
          }

          console.warn(
            '[PaymentService] mini program virtual payment failed; polling will confirm result',
            errCode,
            err?.errMsg
          )
          resolve()
        }
      })
    })
  }

  async fetchMiniProgramVirtualPaymentParams(
    orderId: string,
    options: MiniProgramVirtualPaymentExecuteOptions = {}
  ): Promise<MiniProgramVirtualPaymentResponse> {
    const code = await this.getMiniProgramLoginCode()
    const applet = options.applet || getApplet()
    const configuredEnv = getVirtualPayEnv()
    const env = options.env ?? configuredEnv ?? 0

    return await this.getMiniProgramVirtualPayment({
      order_id: orderId,
      ...(applet ? { applet } : {}),
      code,
      env
    })
  }

  createMiniProgramVirtualPaymentPolling(
    orderId: string,
    options: MiniProgramVirtualPaymentPollingOptions = {}
  ): PaymentPollingHandle {
    const polling = createPolling({
      interval: options.interval ?? 2000,
      maxAttempts: options.maxAttempts ?? 30,
      stopOnError: false
    })

    const promise = polling
      .start(
        async () => this.paymentAPI.checkPaymentStatus(orderId),
        (status) => status.status === 'success'
      )
      .then(() => undefined)
      .catch((error) => {
        if (error?.message === 'Max attempts reached' || error?.message === 'Polling timeout') {
          throw {
            msg: '支付结果确认中，请稍后在会员中心查看',
            timeout: true
          }
        }
        throw error
      })

    return {
      promise,
      stop: () => polling.stop()
    }
  }

  async executeMiniProgramVirtualPayment(
    orderId: string,
    options: MiniProgramVirtualPaymentExecuteOptions = {}
  ): Promise<void> {
    const retryCount = options.retryCount ?? 0

    if (!this.canUseMiniProgramVirtualPayment()) {
      throw { msg: '当前微信版本不支持虚拟支付，请升级微信' }
    }

    const uni = getMiniProgramRuntime()

    try {
      const params = await this.fetchMiniProgramVirtualPaymentParams(orderId, options)

      if (options.showLoadingToast) {
        uni?.showToast?.({
          title: options.loadingTitle || '支付处理中...',
          icon: 'none',
          duration: 60000
        })
      }

      const poll = this.createMiniProgramVirtualPaymentPolling(orderId, {
        interval: options.interval,
        maxAttempts: options.maxAttempts
      })

      let cancelled = false
      const payPromise = this.requestMiniProgramVirtualPayment(params).catch((error: any) => {
        if (error?.cancel) {
          cancelled = true
          poll.stop()
        }

        if (error?.sessionExpired) {
          poll.stop()
          throw error
        }

        // iOS App Store 不可用，停止轮询并直接抛出
        if (error?.appStoreUnavailable) {
          poll.stop()
          throw error
        }

        console.warn(
          '[PaymentService] requestVirtualPayment failed; polling will confirm result',
          JSON.stringify(error)
        )
      })

      const payChain = payPromise.then(() => {
        if (cancelled) throw { msg: '已取消支付', cancel: true }
        return poll.promise
      })

      await Promise.race([poll.promise, payChain])
      payChain.catch(() => {})
    } catch (error: any) {
      if (
        error?.errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_SESSION_EXPIRED_CODE &&
        retryCount < 1
      ) {
        return this.executeMiniProgramVirtualPayment(orderId, {
          ...options,
          retryCount: retryCount + 1
        })
      }
      throw error
    } finally {
      if (options.showLoadingToast) {
        uni?.hideToast?.()
      }
    }
  }

  /**
   * 创建订单并获取支付二维码（组合操作）
   */
  getMiniProgramLoginCode(): Promise<string> {
    return new Promise((resolve, reject) => {
      const uni = getMiniProgramRuntime()
      const wx = getWxRuntime()

      if (uni?.login) {
        uni.login({
          provider: 'weixin',
          success: (res) => resolve(res.code),
          fail: (err) => reject({ msg: err?.errMsg || 'Failed to get login code' })
        })
        return
      }

      if (wx?.login) {
        wx.login({
          success: (res) => resolve(res.code),
          fail: (err) => reject({ msg: err?.errMsg || 'Failed to get login code' })
        })
        return
      }

      reject({ msg: 'Only WeChat Mini Program is supported' })
    })
  }

  requestMiniProgramVirtualPayment(params: MiniProgramVirtualPaymentResponse): Promise<void> {
    return new Promise((resolve, reject) => {
      const wx = getWxRuntime()
      if (!wx?.requestVirtualPayment) {
        reject({ msg: 'Only WeChat Mini Program is supported' })
        return
      }

      if (!this.canUseMiniProgramVirtualPayment()) {
        reject({ msg: 'Current WeChat version does not support virtual payment' })
        return
      }

      // iOS 环境检查（系统版本、微信版本）
      const iosEnvError = checkiOSVirtualPaymentEnv()
      if (iosEnvError) {
        reject({ msg: iosEnvError, appStoreUnavailable: true })
        return
      }

      wx.requestVirtualPayment({
        mode: params.mode,
        signData: params.sign_data,
        paySig: params.pay_sig,
        signature: params.signature,
        success: () => resolve(),
        fail: (err) => {
          const errCode = err?.errCode ?? 0
          if (errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_CANCEL_CODE) {
            reject({ msg: 'Payment cancelled', cancel: true, errCode })
            return
          }

          if (errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_SESSION_EXPIRED_CODE) {
            reject({
              msg: MINI_PROGRAM_VIRTUAL_PAYMENT_ERROR_MAP[errCode],
              sessionExpired: true,
              errCode
            })
            return
          }

          // iOS App Store 不可用（errCode 4 或 errMsg 包含 "App Store"），直接失败不走轮询
          const errMsg = err?.errMsg || ''
          if (errCode === 4 || errMsg.includes('App Store')) {
            reject({
              msg: '暂时无法提供支付',
              appStoreUnavailable: true,
              errCode
            })
            return
          }

          console.warn(
            '[PaymentService] mini program virtual payment failed; polling will confirm result',
            errCode,
            err?.errMsg
          )
          resolve()
        }
      })
    })
  }

  async fetchMiniProgramVirtualPaymentParams(
    orderId: string,
    options: MiniProgramVirtualPaymentExecuteOptions = {}
  ): Promise<MiniProgramVirtualPaymentResponse> {
    const code = await this.getMiniProgramLoginCode()
    const applet = options.applet || getApplet()
    const configuredEnv = getVirtualPayEnv()
    const env = options.env ?? configuredEnv ?? 0

    return await this.getMiniProgramVirtualPayment({
      order_id: orderId,
      ...(applet ? { applet } : {}),
      code,
      env
    })
  }

  createMiniProgramVirtualPaymentPolling(
    orderId: string,
    options: MiniProgramVirtualPaymentPollingOptions = {}
  ): PaymentPollingHandle {
    const polling = createPolling({
      interval: options.interval ?? 2000,
      maxAttempts: options.maxAttempts ?? 30,
      stopOnError: false
    })

    const promise = polling
      .start(
        async () => this.paymentAPI.checkPaymentStatus(orderId),
        (status) => status.status === 'success'
      )
      .then(() => undefined)
      .catch((error) => {
        if (error?.message === 'Max attempts reached' || error?.message === 'Polling timeout') {
          throw {
            msg: 'Payment result is still confirming',
            timeout: true
          }
        }
        throw error
      })

    return {
      promise,
      stop: () => polling.stop()
    }
  }

  async executeMiniProgramVirtualPayment(
    orderId: string,
    options: MiniProgramVirtualPaymentExecuteOptions = {}
  ): Promise<void> {
    const retryCount = options.retryCount ?? 0

    if (!this.canUseMiniProgramVirtualPayment()) {
      throw { msg: 'Current WeChat version does not support virtual payment' }
    }

    const uni = getMiniProgramRuntime()

    try {
      const params = await this.fetchMiniProgramVirtualPaymentParams(orderId, options)

      if (options.showLoadingToast) {
        uni?.showToast?.({
          title: options.loadingTitle || 'Processing payment...',
          icon: 'none',
          duration: 60000
        })
      }

      const poll = this.createMiniProgramVirtualPaymentPolling(orderId, {
        interval: options.interval,
        maxAttempts: options.maxAttempts
      })

      let cancelled = false
      const payPromise = this.requestMiniProgramVirtualPayment(params).catch((error: any) => {
        if (error?.cancel) {
          cancelled = true
          poll.stop()
        }

        if (error?.sessionExpired) {
          poll.stop()
          throw error
        }

        // iOS App Store 不可用，停止轮询并直接抛出
        if (error?.appStoreUnavailable) {
          poll.stop()
          throw error
        }

        console.warn(
          '[PaymentService] requestVirtualPayment failed; polling will confirm result',
          JSON.stringify(error)
        )
      })

      const payChain = payPromise.then(() => {
        if (cancelled) throw { msg: 'Payment cancelled', cancel: true }
        return poll.promise
      })

      await Promise.race([poll.promise, payChain])
      payChain.catch(() => {})
    } catch (error: any) {
      if (
        error?.errCode === MINI_PROGRAM_VIRTUAL_PAYMENT_SESSION_EXPIRED_CODE &&
        retryCount < 1
      ) {
        return this.executeMiniProgramVirtualPayment(orderId, {
          ...options,
          retryCount: retryCount + 1
        })
      }
      throw error
    } finally {
      if (options.showLoadingToast) {
        uni?.hideToast?.()
      }
    }
  }

  async createOrderAndGetQRCode(params: CreateOrderParams): Promise<{
    order: CreateOrderResponse
    qrCode: PaymentQRCode
  }> {
    try {
      // 创建订单
      const order = await this.createOrder(params)

      // 获取支付二维码（使用创建订单时的参数）
      const qrCode = await this.getPaymentQRCode({
        plan_type: params.plan_type || 1,
        goods_id: order.order.package_id,
        coupon_code: params.coupon_code
      })

      return { order, qrCode }
    } catch (error) {
      console.error('[PaymentService] Failed to create order and get QR code', error)
      throw error
    }
  }

  /**
   * 清除支付信息
   */
  clearPaymentInfo(): void {
    this.stopPaymentPolling()
    this.paymentStore.clearPaymentInfo()
  }

  /**
   * 清除所有数据
   */
  clearAll(): void {
    this.stopPaymentPolling()
    this.paymentStore.clearAll()
  }

  /**
   * 获取当前套餐列表（从 store）
   */
  getCurrentPackages(): Package[] {
    return this.paymentStore.packages
  }

  /**
   * 获取当前订单（从 store）
   */
  getCurrentOrder() {
    return this.paymentStore.currentOrder
  }

  /**
   * 获取当前支付二维码（从 store）
   */
  getCurrentPaymentQRCode() {
    return this.paymentStore.paymentQRCode
  }

  /**
   * 获取当前优惠券列表（从 store）
   */
  getCurrentCoupons(): Coupon[] {
    return this.paymentStore.coupons
  }

  /**
   * 获取可用优惠券列表
   */
  getAvailableCoupons(): Coupon[] {
    return this.paymentStore.availableCoupons.value
  }

  /**
   * 根据 ID 获取套餐
   */
  getPackageById(id: number): Package | undefined {
    return this.paymentStore.getPackageById(id)
  }

  /**
   * 根据类型获取套餐列表
   */
  getPackagesByType(type: string): Package[] {
    return this.paymentStore.getPackagesByType(type)
  }

  /**
   * 分析移动端 H5 支付处理方式
   * SDK 不执行页面跳转，只返回调用方需要处理的支付类型和订单信息。
   *
   * 调用方应按以下顺序处理：
   * 1. 判断小程序/App WebView → 跳转原生支付页（不调用此方法）
   * 2. 判断桌面软件 → 通知原生端（不调用此方法）
   * 3. 判断 res.mobile → 调用此方法获取 H5 支付信息
   * 4. 否则为 PC 端 → 显示二维码（不调用此方法）
   *
   * @param data - 支付二维码数据（需要有 mobile 标记）
   * @returns 跳转结果
   * @deprecated 请使用 processPayment 方法，它会统一返回各环境的支付处理结果
   */
  /**
   * 处理移动端 H5 支付跳转
   * 仅处理移动端 H5 环境的跳转，小程序/App 环境应由调用方在调用此方法前判断并处理
   *
   * 调用方应按以下顺序处理：
   * 1. 判断小程序/App WebView → 跳转原生支付页（不调用此方法）
   * 2. 判断桌面软件 → 通知原生端（不调用此方法）
   * 3. 判断 res.mobile → 调用此方法处理 H5 跳转
   * 4. 否则为 PC 端 → 显示二维码（不调用此方法）
   *
   * @param data - 支付二维码数据（需要有 mobile 标记）
   * @returns 跳转结果
   * @deprecated 请使用 processPayment 方法，它会自动处理所有环境
   */
  handlePaymentRedirect(data: PaymentQRCode): PaymentRedirectResult {
    // 无 mobile 标记说明是 PC 端数据，不处理
    if (!(data as any).mobile) {
      return { handled: false, type: 'pc' }
    }

    const orderId = (data as any).id || data.order_id

    // 小程序 WebView 环境
    if (isMiniWechatWebView()) {
      const callback = getNativePayRedirect()
      if (callback) {
        callback(orderId, 'mini')
      } else {
        console.warn('[PaymentService] onNativePayRedirect 未配置，无法跳转小程序支付')
      }
      return { handled: true, type: 'native', orderId: String(orderId) }
    }

    // App WebView 环境
    if (isAppWebView()) {
      const callback = getNativePayRedirect()
      if (callback) {
        callback(orderId, 'app')
      } else {
        console.warn('[PaymentService] onNativePayRedirect 未配置，无法跳转 App 支付')
      }
      return { handled: true, type: 'native', orderId: String(orderId) }
    }

    // H5 移动端环境
    const softId = getSoftId()
    const ekey = this.storage.getItem<string>(STORAGE_KEYS.EKEY) || ''
    const source = this.storage.getItem<string>(STORAGE_KEYS.SOURCE) || ''

    console.log('[PaymentService] handlePaymentRedirect orderId:', orderId)

    // 保存支付状态
    this.storage.setItem('isPay', JSON.stringify({ orderId }))

    // 微信环境使用 wxMobilePayUrl
    if (isWeChat()) {
      const wxUrl = getWxMobilePayUrl() || 'https://api.360qnw.com'
      window.location.href = `${wxUrl}/api/show-order?order_id=${orderId}&soft_id=${softId}&ekey=${ekey}&source=${source}`
    } else {
      // 非微信环境使用 mobilePayUrl
      const mobileUrl = getMobilePayUrl() || 'https://api.360qnw.com'
      window.location.href = `${mobileUrl}/pay/pay.html?order_id=${orderId}&skip=order&soft_id=${softId}&ekey=${ekey}&source=${source}`
    }

    return { handled: true, type: 'h5', orderId: String(orderId) }
  }

  /**
   * 统一支付处理入口
   * 自动判断环境并返回对应的支付处理结果，页面跳转、原生通信和 UI 展示由业务项目处理。
   *
   * 处理顺序：
   * 1. 小程序/App WebView → 返回 native 类型，由调用方跳转原生支付页
   * 2. 桌面软件 → 返回桌面支付参数，由调用方发送 win_message
   * 3. 移动端 H5 → 返回 H5 支付信息，由调用方跳转
   * 4. PC 端 → 返回二维码图片，由调用方展示
   *
   * @param data - 支付二维码数据
   * @returns 支付处理结果
   */
  /**
   * 统一支付处理入口
   * 自动判断环境并执行对应的支付流程，调用方只需根据返回结果处理后续逻辑
   *
   * 处理顺序（与旧版一致）：
   * 1. 小程序/App WebView → 跳转原生支付页
   * 2. 桌面软件 → 返回桌面支付参数，由调用方发送 win_message
   * 3. 移动端 H5 → 跳转 H5 支付页面
   * 4. PC 端 → 返回二维码图片
   *
   * @param data - 支付二维码数据
   * @returns 支付处理结果
   */
  async processPayment(data: PaymentQRCode): Promise<PaymentRedirectResult> {
    const orderId = (data as any).id || data.order_id
    const isDesktopApp =
      this.storage.getItem<string>('isDesktop') === '1' &&
      !!this.storage.getItem<string>('isTransport')

    console.log('[PaymentService] processPayment orderId:', orderId)

    // 1. 小程序 WebView 环境
    if (isMiniWechatWebView()) {
      const callback = getNativePayRedirect()
      if (callback) {
        callback(orderId, 'mini')
      } else {
        console.warn('[PaymentService] onNativePayRedirect 未配置，无法跳转小程序支付')
      }
      return { handled: true, type: 'native', orderId: String(orderId) }
    }

    // 2. App WebView 环境
    if (isAppWebView()) {
      const callback = getNativePayRedirect()
      if (callback) {
        callback(orderId, 'app')
      } else {
        console.warn('[PaymentService] onNativePayRedirect 未配置，无法跳转 App 支付')
      }
      return { handled: true, type: 'native', orderId: String(orderId) }
    }

    // 3. 桌面软件环境
    if (isDesktopApp) {
      // 返回桌面支付类型，由调用方处理 win_message 和轮询
      return {
        handled: false,
        type: 'desktop',
        orderId: String(orderId)
      }
    }

    // 4. 移动端 H5（有 mobile 标记）
    if ((data as any).mobile) {
      // 保存支付状态，用于支付完成后回调
      this.storage.setItem('isPay', JSON.stringify({ orderId }))

      const softId = getSoftId()
      const ekey = this.storage.getItem<string>(STORAGE_KEYS.EKEY) || ''
      const source = this.storage.getItem<string>(STORAGE_KEYS.SOURCE) || ''

      // 微信环境使用 wxMobilePayUrl
      if (isWeChat()) {
        const wxUrl = getWxMobilePayUrl() || 'https://api.360qnw.com'
        window.location.href = `${wxUrl}/api/show-order?order_id=${orderId}&soft_id=${softId}&ekey=${ekey}&source=${source}`
      } else {
        // 非微信环境使用 mobilePayUrl
        const mobileUrl = getMobilePayUrl() || 'https://api.360qnw.com'
        window.location.href = `${mobileUrl}/pay/pay.html?order_id=${orderId}&skip=order&soft_id=${softId}&ekey=${ekey}&source=${source}`
      }

      return { handled: true, type: 'h5', orderId: String(orderId) }
    }

    // 5. PC 端：生成二维码图片
    const qrCodeImage = await this.resolveQRCodeImage(data)
    return {
      handled: false,
      type: 'pc',
      orderId: String(orderId),
      qrCodeImage
    }
  }

  /**
   * 将支付链接转换为二维码图片
   * 后端返回的支付链接（weixin://、alipay:// 等协议）不能直接作为 img src
   * 需要用 QRCode 库转成 base64 图片
   *
   * @param data - 支付二维码数据或支付链接字符串
   * @returns base64 格式的二维码图片
   */
  async resolveQRCodeImage(data: PaymentQRCode | string): Promise<string> {
    let url: string

    if (typeof data === 'string') {
      url = data
    } else {
      // 优先级：order_url > qr_code_url > url
      url = data.order_url || data.qr_code_url || data.url || ''
    }

    if (!url) {
      throw new Error('支付链接为空')
    }

    try {
      return await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      })
    } catch (error) {
      console.error('[PaymentService] 生成二维码失败', error)
      throw error
    }
  }
}
